import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { useEffect, useState } from "react";
import { LuCalendar, LuEye, LuLinkedin } from "react-icons/lu";
import SearchBar from "../../../common/Search";
import CustomPagination from "../../../components/Common/CustomPagination";
import { apiGet, toastError } from "../../../utils";
import PartnerDetailsModal from "./PartnerDetailsModal";

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#ffffff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 250,
    fontSize: theme.typography.pxToRem(11),
    border: '1px solid #dadde9',
    boxShadow: theme.shadows[3],
    padding: '8px',
    borderRadius: '0px'
  },
}))(Tooltip);

const renderAvailability = (availabilityWeekly) => {
  if (!availabilityWeekly) return <div className="text-center text-slate-500 py-2">No availability set</div>;

  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const hasAvailableDay = days.some(d => availabilityWeekly[d]?.isAvailable);

  if (!hasAvailableDay) {
    return <div className="text-center text-slate-500 py-2">No availability set</div>;
  }

  return (
    <div className="flex flex-col gap-2 w-48">
      <Typography variant="subtitle2" className="text-[11px] font-medium border-b border-slate-200 pb-1 mb-1 text-slate-800">Weekly Availability</Typography>
      {days.map((day) => {
        const data = availabilityWeekly[day];
        if (!data || !data.isAvailable) return null;
        return (
          <div key={day} className="flex justify-between items-center text-[11px]">
            <span className="capitalize font-medium text-slate-700">{day.substring(0, 3)}</span>
            <span className="text-slate-600 bg-slate-50 px-2 py-0.5 border border-slate-200">{data.startTime} - {data.endTime}</span>
          </div>
        );
      })}
    </div>
  );
};

export default function AdminKnowledgePartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);

  const [filters, setFilters] = useState({
    offset: 1,
    limit: 20,
    search: "",
    startDate: null,
    endDate: null,
  });
  const [totalPage, setTotalPage] = useState(0);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      fetchPartners();
    }, 500);
    return () => clearTimeout(timer);
  }, [filters]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("page", filters.offset);
      queryParams.append("limit", filters.limit);
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.startDate) queryParams.append("startDate", filters.startDate);
      if (filters.endDate) queryParams.append("endDate", filters.endDate);

      const response = await apiGet(`/api/admin/knowledge-partner/list?${queryParams.toString()}`);
      if (response && response.data) {
        setPartners(response.data.list || []);
        setTotalPage(response.data.total || 0);
      }
    } catch (err) {
      toastError(err?.message || "Failed to fetch Knowledge Partners");
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (partner) => {
    setSelectedPartner(partner);
  };

  return (
    <div className="min-h-[calc(100vh-106px)] flex flex-col">


      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-2 w-full mb-2">
        <div className="w-full md:w-96 flex-shrink-0 overflow-hidden [&_.MuiInputBase-root]:w-full [&_input]:!w-full">
          <SearchBar
            isMobile={false}
            placeholder="Search by name or email..."
            searchValue={filters.search}
            setSearchValue={(e) => setFilters({ ...filters, search: e.target.value, offset: 1 })}
            onDateRangeChange={({ startDate, endDate }) => setFilters({ ...filters, startDate, endDate, offset: 1 })}
          />
        </div>
      </div>

      <Box className="overflow-hidden w-full">
        <TableContainer component={Paper} className="!shadow-sm !rounded-none overflow-auto scroll-thin w-full">
          {totalPage > filters.limit && (
            <CustomPagination
              filters={filters}
              totalPage={totalPage}
              onChangePagination={(type) => {
                let newOffset = filters.offset;
                if (type === "first") newOffset = 1;
                else if (type === "dec" && newOffset > 1) newOffset -= 1;
                else if (type === "inc" && newOffset * filters.limit < totalPage) newOffset += 1;
                setFilters({ ...filters, offset: newOffset });
              }}
            />
          )}
          <Table stickyHeader className="universal-table w-full" size="small" style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell className="font-medium">Partner</TableCell>
                <TableCell className="font-medium">Email</TableCell>
                <TableCell className="font-medium">Google Calendar</TableCell>
                <TableCell align="center" className="font-medium">LinkedIn</TableCell>
                <TableCell align="center" className="font-medium">Availability</TableCell>
                <TableCell align="center" className="font-medium">Demo Calls</TableCell>
                <TableCell align="center" className="font-medium">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" className="!py-8 !bg-white">
                    <CircularProgress size={24} className="text-[#313C4C]" />
                  </TableCell>
                </TableRow>
              ) : partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography className="text-[11px] py-3" color="textSecondary">
                      No Knowledge Partners found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                partners.map((partner) => (
                  <TableRow
                    hover
                    key={partner._id}
                    onClick={() => handleRowClick(partner)}
                    className="h-7 cursor-pointer"
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 flex-shrink-0 rounded-full bg-[#313C4C]/10 text-[#313C4C] flex items-center justify-center overflow-hidden">
                          {partner.profileImage ? (
                            <img src={partner.profileImage} alt={partner.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="font-bold">{partner.name?.charAt(0)?.toUpperCase()}</span>
                          )}
                        </div>
                        <Typography className="text-[11px] font-medium">
                          {partner.name}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>{partner.email}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex h-[20px] items-center px-2 text-[11px] font-medium whitespace-nowrap border ${partner.googleConnected
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-700 border-slate-200"
                          }`}
                      >
                        {partner.googleConnected ? "Connected" : "Not connected"}
                      </span>
                    </TableCell>

                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {partner.linkedinUrl ? (
                        <IconButton
                          component="a"
                          href={partner.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          className="text-[#313C4C]"
                          aria-label={`Open LinkedIn profile for ${partner.name}`}
                        >
                          <LuLinkedin className="w-3.5 h-3.5" />
                        </IconButton>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </TableCell>

                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <HtmlTooltip title={renderAvailability(partner.availabilityWeekly)} placement="left">
                        <IconButton size="small" className="text-slate-500" aria-label={`Weekly availability for ${partner.name}`}>
                          <LuCalendar className="w-3.5 h-3.5" />
                        </IconButton>
                      </HtmlTooltip>
                    </TableCell>


                    <TableCell align="center">
                      <span className="inline-flex items-center justify-center min-w-[24px] h-[20px] px-1 border border-slate-200 bg-slate-50 text-slate-700 font-medium text-[11px] tabular-nums">
                        {partner.totalCalls || 0}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" className="text-[#313C4C]" aria-label={`View details for ${partner.name}`}>
                        <LuEye className="w-3.5 h-3.5" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalPage > filters.limit && (
            <CustomPagination
              filters={filters}
              totalPage={totalPage}
              onChangePagination={(type) => {
                let newOffset = filters.offset;
                if (type === "first") newOffset = 1;
                else if (type === "dec" && newOffset > 1) newOffset -= 1;
                else if (type === "inc" && newOffset * filters.limit < totalPage) newOffset += 1;
                setFilters({ ...filters, offset: newOffset });
              }}
            />
          )}
        </TableContainer>
      </Box>

      <PartnerDetailsModal
        open={Boolean(selectedPartner)}
        handleClose={() => setSelectedPartner(null)}
        partner={selectedPartner}
      />
    </div >
  );
}
